<template>
  <div class="c-popularity-and-engagement-icons">
    <NTooltip
      v-if="isRecommended"
      class="!bg-base !color-text"
      :style="{
        '--n-color': themeVars.base,
      }"
      :trigger="isTouchScreen ? 'click' : 'hover'"
    >
      <template #trigger>
        <div
          class="flex items-center justify-center w-[24px] h-[24px] rounded-full"
          :style="{
            background: themeVars.primary6,
            border: `1px solid ${themeVars.primary5}`,
            color: themeVars.primary,
          }"
          @click.stop
        >
          <CIcon class="relative -left-[1px]" fa-type="fas" icon="star" size="12px" />
        </div>
      </template>
      {{ t('canvasUI.CPopularityAndEngagementIcons.recommended') }}
    </NTooltip>
    <NTooltip
      v-if="(popularity?.value && popularity.value > 0) || views > 0"
      class="!bg-base !color-text"
      :style="{
        '--n-color': themeVars.base,
      }"
      :trigger="isTouchScreen ? 'click' : 'hover'"
    >
      <template #trigger>
        <div
          class="flex items-center justify-center w-[24px] h-[24px] rounded-full"
          :class="getPopularityClass()"
          :style="{
            border:
              popularity?.category === SectionPopularityCategory.NONE
                ? `1px solid ${themeVars.primary5}`
                : '1px solid transparent',
          }"
          @click.stop
        >
          <CIcon class="w-full" :icon="getPopularityIcon()" />
        </div>
      </template>

      <span v-if="popularity?.value">
        {{ popularity?.value }}% {{ t('canvasUI.CPopularityAndEngagementIcons.popularityScore') }},
      </span>

      <span v-if="views">{{ t('canvasUI.CPopularityAndEngagementIcons.views') }}: {{ views }}</span>
    </NTooltip>
    <NTooltip
      v-if="hasEngagementData"
      class="popularity-tooltip !bg-base !color-text"
      :style="{
        '--n-color': themeVars.base,
      }"
      :trigger="isTouchScreen ? 'click' : 'hover'"
    >
      <template #trigger>
        <div
          class="flex items-center justify-center w-[24px] h-[24px] rounded-full"
          :class="getEngagementClass()"
          :style="{
            border:
              engagement?.direction !== SectionTrendlineDirection.ASC
                ? `1px solid ${themeVars.primary5}`
                : '1px solid transparent',
          }"
          @click.stop
        >
          <CIcon class="w-full" icon="wave-pulse" />
        </div>
      </template>

      <CApexChartLine :height="180" :options="chartOptions" :series="chartData" :width="300" />
    </NTooltip>
  </div>
</template>
<script setup lang="ts">
import { computed, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { NTooltip } from 'naive-ui'
import type { ApexOptions } from 'apexcharts'

import CIcon from '@lib/components/CIcon/CIcon.vue'
import CApexChartLine from '@lib/components/CApexChart/CApexChartLine.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import { isTouchScreen } from '@lib/util/userAgents'
import {
  SectionPopularityCategory,
  SectionTrendlineDirection,
  type SectionEngagement,
  type SectionPopularity,
} from '@lib/types/sectionPopularity'
import { dayjs, formatDayMonthBasedOnBrowserLang } from '@lib/util/date'

interface Props {
  isRecommended?: boolean
  isTheMostPopular?: boolean
  popularity: SectionPopularity | undefined
  engagement: SectionEngagement | undefined
  views: number
}

const props = defineProps<Props>()

const { t } = useI18n()
const themeVars = useThemeVars()

const hasEngagementData = computed(() => props.engagement?.data.some((value) => typeof value === 'number' && value > 0))

const chartData: ComputedRef<ApexAxisChartSeries> = computed(() => [
  {
    data: props.engagement?.data ?? [],
    name: t('canvasUI.CPopularityAndEngagementIcons.views'),
  },
])

function generateDates(daysCount: number) {
  const dates: string[] = []
  for (let i = 0; i < daysCount; i++) {
    const date = dayjs()
      .subtract(i + 1, 'day')
      .toDate()
    dates.unshift(formatDayMonthBasedOnBrowserLang(date))
  }
  return dates
}

const chartOptions: ComputedRef<ApexOptions> = computed(() => ({
  chart: {
    animations: {
      speed: 500,
    },
    zoom: {
      enabled: false,
    },
    toolbar: {
      show: false,
    },
  },
  tooltip: {
    x: {
      show: false,
    },
  },
  colors: [
    props.engagement?.direction === SectionTrendlineDirection.ASC ? themeVars.value.success : themeVars.value.error,
  ],
  dataLabels: {
    enabled: false,
  },
  stroke: {
    curve: 'smooth',
    width: 2,
  },
  markers: {
    size: 5,
    strokeColors: '#fff',
    strokeWidth: 2,
    hover: {
      size: 7,
    },
  },
  xaxis: {
    categories: generateDates(props.engagement?.data.length ?? 0),
  },
  yaxis: {
    min: 0,
    forceNiceScale: true,
    decimalsInFloat: 0,
    labels: {
      formatter: function (val: number) {
        return val.toFixed(0)
      },
    },
  },
}))

function getPopularityClass(): string {
  switch (props.popularity?.category) {
    case SectionPopularityCategory.NONE:
      return 'color-primary2 bg-primary6'
    case SectionPopularityCategory.POPULAR:
      return 'color-info bg-info2'
    case SectionPopularityCategory.THE_MOST_POPULAR:
      return 'color-base bg-info'
    default:
      return 'color-primary2 bg-primary6'
  }
}

function getEngagementClass(): string {
  if (props.engagement?.direction === SectionTrendlineDirection.ASC) return 'color-success bg-success2'
  else return 'color-primary2 bg-primary6'
}

function getPopularityIcon() {
  switch (props.popularity?.category) {
    case SectionPopularityCategory.NONE:
    case SectionPopularityCategory.POPULAR:
      return 'fire'
    case SectionPopularityCategory.THE_MOST_POPULAR:
      return 'crown'
    default:
      return 'fire'
  }
}
</script>
